import React, { useMemo } from 'react';
import moment from 'moment';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import {
	CreateInsuranceCompanyBatchesInput,
	InsuranceCompanyBatchRegisterType,
	PaginationOrderHow,
	useCreateInsuranceCompanyBatchesMutation,
	useInsuranceCompanyBatchTypesQuery,
} from '../../../../../../graphql/graphql';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import styles from './ManagerInsuranceCompaniesBatchCreateForm.module.scss';
import useLoading from '../../../../../Base/Hooks/useLoading';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useWorkplaceMeta from '../../../../../Workplace/Hooks/useWorkplaceMeta';
import { useAppContext } from '../../../../../../Services/AppContext';
import { InsuranceCompanyBatch } from '../../../../Consts/InsuranceCompanyBatchesDefaultColumns.const';

/** Matches backend BATCH_TYPE.REGISTER / dtyp stored in DB */
const REGISTER_BATCH_DTYP = '80';

interface Props {
	onSuccess: (batches: InsuranceCompanyBatch[]) => void;
	reloadData: () => void;
}

interface SubmitValues {
	insuranceCompanyIds: SelectValue[];
	insuranceCompanyBranchCode: { label: string; value: string };
	batchTypes: SelectValue[];
	registerType?: { label: string; value: InsuranceCompanyBatchRegisterType };
	periodYear: string | number;
	periodMonth: string | number;
}

const DEFAULT_VALUES = {
	char: 'P', // - prvotna davka, ked budeme podporovat opravne davky, odstranit
};

const getSelectValues = (values?: SelectValue[]) =>
	values?.map(item => item.value as string) ?? [];

const ManagerInsuranceCompaniesBatchCreateForm = ({
	onSuccess,
	reloadData,
}: Props) => {
	const { isLoading, setIsLoading } = useLoading();

	const { displayGQLError, showUnexpectedError } = useApiClient();
	const [createInsuranceCompanyBatches] =
		useCreateInsuranceCompanyBatchesMutation();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { workplaceId } = useAppContext();
	const { workplaceMeta } = useWorkplaceMeta({ workplaceId });
	const { data: batchTypesData } = useInsuranceCompanyBatchTypesQuery({
		pagination: {
			take: 999,
			order: {
				key: 'value',
				how: PaginationOrderHow.Asc,
			},
		},
	});

	const onSubmit = async (values: SubmitValues) => {
		const batchTypeIds = getSelectValues(values.batchTypes);
		const batchTypes = batchTypesData?.insuranceCompanyBatchTypes?.data || [];
		const hasRegisterBatch = batchTypeIds.some(batchTypeId =>
			batchTypes.some(
				batchType =>
					batchType?.id === batchTypeId &&
					batchType.value === REGISTER_BATCH_DTYP,
			),
		);

		if (hasRegisterBatch && !values.registerType?.value) {
			showNotificationAlert({
				content:
					'component.insuranceCompanyBatch.createForm.registerTypeRequired',
			});
			return;
		}

		setIsLoading(true);

		const input: CreateInsuranceCompanyBatchesInput = {
			char: DEFAULT_VALUES.char,
			insuranceCompanyBranchId: values.insuranceCompanyBranchCode.value,
			insuranceCompanyIds: getSelectValues(values.insuranceCompanyIds),
			batchTypeIds,
			periodMonth: Number(values.periodMonth),
			periodYear: Number(values.periodYear),
			registerType: hasRegisterBatch ? values.registerType?.value : undefined,
		};

		try {
			const response = await createInsuranceCompanyBatches({ input });

			if ('data' in response && response.data) {
				showNotificationSuccess({
					content: 'component.insuranceCompanyBatch.createForm.successMessage',
				});
				onSuccess(response.data.createInsuranceCompanyBatches);
			} else {
				reloadData();
			}

			displayGQLError(response);
		} catch (error) {
			showUnexpectedError();
			reloadData();
		} finally {
			setIsLoading(false);
		}
	};

	const formScheme: ItemScheme[] = useMemo(() => {
		const currentYear = moment().year();
		const years = Array.from({ length: 10 }, (_, i) => ({
			key: String(currentYear - i),
			value: String(currentYear - i),
			title: String(currentYear - i),
		}));

		return [
			{
				key: 'insuranceCompanyIds',
				title: 'component.insuranceCompanyBatch.table.insuranceCompanyCode',
				type: MagicField.INSURANCE_COMPANIES_MULTISELECT_WITH_CHECKLIST,
				settings: { required: true, portal: true },
			},
			{
				key: 'insuranceCompanyBranchCode',
				title:
					'component.insuranceCompanyBatch.createForm.insuranceCompanyBranchCode',
				type: MagicField.TERM_ITEM_SELECT,
				settings: {
					required: true,
					portal: true,
					termType: 'insurance_company_branch',
					labelFormat: '{value} - {name}',
				},
			},
			{
				key: 'batchTypes',
				title: 'component.insuranceCompanyBatch.createForm.batchTypes',
				type: MagicField.INSURANCE_COMPANY_BATCH_TYPES_MULTISELECT_WITH_CHECKLIST,
				settings: {
					required: true,
					portal: true,
				},
			},
			{
				key: 'registerType',
				title: 'component.insuranceCompanyBatch.createForm.registerType',
				type: MagicField.SELECT,
				settings: { portal: true },
				children: [
					{
						key: InsuranceCompanyBatchRegisterType.Initial,
						value: InsuranceCompanyBatchRegisterType.Initial,
						title:
							'component.insuranceCompanyBatch.createForm.registerTypeInitial',
					},
					{
						key: InsuranceCompanyBatchRegisterType.Update,
						value: InsuranceCompanyBatchRegisterType.Update,
						title:
							'component.insuranceCompanyBatch.createForm.registerTypeUpdate',
					},
				],
			},
			{
				key: 'periodYear',
				title: 'component.insuranceCompanyBatch.table.periodYear',
				type: MagicField.SELECT,
				value: String(currentYear),
				children: years,
				settings: { required: true, portal: true, returnKey: 'value' },
			},
			{
				key: 'periodMonth',
				title: 'component.insuranceCompanyBatch.table.periodMonth',
				type: MagicField.SELECT,
				value: String(moment().month() + 1),
				children: [
					{ key: '1', value: '1', title: 'base.months.january' },
					{ key: '2', value: '2', title: 'base.months.february' },
					{ key: '3', value: '3', title: 'base.months.march' },
					{ key: '4', value: '4', title: 'base.months.april' },
					{ key: '5', value: '5', title: 'base.months.may' },
					{ key: '6', value: '6', title: 'base.months.june' },
					{ key: '7', value: '7', title: 'base.months.july' },
					{ key: '8', value: '8', title: 'base.months.august' },
					{ key: '9', value: '9', title: 'base.months.september' },
					{ key: '10', value: '10', title: 'base.months.october' },
					{ key: '11', value: '11', title: 'base.months.november' },
					{ key: '12', value: '12', title: 'base.months.december' },
				],
				settings: { required: true, portal: true, returnKey: 'value' },
			},
		];
	}, []);

	const defaultValues = useMemo(
		() => ({
			insuranceCompanyBranchCode:
				workplaceMeta?.workplaceInsuranceCompanyBranch,
		}),
		[workplaceMeta],
	);

	return (
		<div className={styles.wrapper}>
			<MagicForm
				formScheme={formScheme}
				defaultValues={defaultValues}
				onSubmit={onSubmit}
				submitText={'base.shared.form.send'}
				disableSubmit={isLoading}
			/>
		</div>
	);
};

export default ManagerInsuranceCompaniesBatchCreateForm;
